import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styles from './AffiliateAdmin.module.css';

type Affiliate = {
  id: string;
  name: string;
  email: string;
  code: string;
  referred_by?: string;
  date_created?: string;
  date_activated?: string | null;
};

type AffiliateFormData = {
  name: string;
  email: string;
  code: string;
  date_activated: string | null;
  referred_by: string;
};

type SubmitEventLike = { preventDefault: () => void };

async function apiFetchAffiliates(): Promise<Affiliate[]> {
  const response = await fetch('/api/affiliates');
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  return response.json() as Promise<Affiliate[]>;
}

export default function AffiliateAdmin() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<AffiliateFormData>({
    name: '',
    email: '',
    code: '',
    date_activated: null,
    referred_by: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);

  const { data: affiliates = [], isLoading } = useQuery({
    queryKey: ['affiliates'],
    queryFn: apiFetchAffiliates,
  });

  const saveMutation = useMutation({
    mutationFn: async (vars: { formData: AffiliateFormData; editMode: boolean; currentId: string | null }) => {
      const url = vars.editMode ? `/api/affiliates/${vars.currentId}` : '/api/affiliates';
      const method = vars.editMode ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars.formData),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save affiliate');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
      resetForm();
    },
    onError: (err: Error) => setMessage(err.message || 'Failed to save affiliate'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/affiliates/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete affiliate');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['affiliates'] }),
    onError: (err: Error) => setMessage(err.message || 'Failed to delete affiliate'),
  });

  const activationMutation = useMutation({
    mutationFn: async (affiliate: Affiliate) => {
      const newActivationDate = affiliate.date_activated ? null : new Date().toISOString();
      const response = await fetch(`/api/affiliates/${affiliate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...affiliate, date_activated: newActivationDate }),
      });
      if (!response.ok) throw new Error(`Failed to update affiliate: ${response.status}`);
      return newActivationDate;
    },
    onSuccess: (newActivationDate, affiliate) => {
      queryClient.setQueryData<Affiliate[]>(['affiliates'], prev =>
        (prev ?? []).map(a => a.id === affiliate.id ? { ...a, date_activated: newActivationDate } : a)
      );
      setMessage(affiliate.date_activated ? 'Affiliate deactivated successfully' : 'Affiliate activated successfully');
    },
    onError: (err: Error) => setMessage(err.message || 'Failed to change activation'),
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', code: '', date_activated: null, referred_by: '' });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = (e: SubmitEventLike) => {
    e.preventDefault();
    saveMutation.mutate({ formData, editMode, currentId });
  };

  const handleEdit = (affiliate: Affiliate) => {
    setFormData({
      name: affiliate.name,
      email: affiliate.email,
      code: affiliate.code,
      date_activated: affiliate.date_activated ?? null,
      referred_by: affiliate.referred_by || '',
    });
    setEditMode(true);
    setCurrentId(affiliate.id);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Are you sure you want to delete this affiliate?')) return;
    deleteMutation.mutate(id);
  };

  const handleActivationChange = (affiliate: Affiliate) => {
    setMessage(null);
    activationMutation.mutate(affiliate);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <h1 className={styles.title}>Affiliate Management</h1>

      <div className={styles.content}>
        <Link to="/marketing-suggestions" className={styles.marketingLink}>
          View Marketing Suggestions &amp; Hashtag Library &rarr;
        </Link>

        <div className={styles.formSection}>
          <h2>{editMode ? 'Edit Affiliate' : 'Add New Affiliate'}</h2>

          {message && <div className={styles.errorMessage}>{message}</div>}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="code">Affiliate Code</label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                required
                disabled={editMode}
              />
              {editMode && <small>Affiliate code cannot be changed once created</small>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="referred_by">Referred By (optional)</label>
              <input
                type="email"
                id="referred_by"
                name="referred_by"
                value={formData.referred_by}
                onChange={handleInputChange}
                placeholder="Email of referring affiliate"
              />
              <small>Email of the affiliate who referred this person</small>
            </div>

            <div className={styles.formActions}>
              <button type="submit" className={styles.submitButton}>
                {editMode ? 'Update Affiliate' : 'Add Affiliate'}
              </button>

              {editMode && (
                <button type="button" className={styles.cancelButton} onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className={styles.listSection}>
          <h2>Affiliate List</h2>

          {isLoading ? (
            <div className={styles.loading}>Loading affiliates...</div>
          ) : affiliates.length === 0 ? (
            <div className={styles.emptyMessage}>No affiliates found</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Code</th>
                  <th>Referred By</th>
                  <th>Created</th>
                  <th>Activated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map(affiliate => {
                  // Track activation status explicitly
                  const isActive = !!affiliate.date_activated;

                  return (
                    <tr
                      key={affiliate.id}
                      className={isActive ? styles.activeRow : styles.inactiveRow}
                    >
                      <td>{affiliate.name}</td>
                      <td>{affiliate.email}</td>
                      <td className={styles.codeCell}>{affiliate.code}</td>
                      <td>{affiliate.referred_by || '-'}</td>
                      <td>{formatDate(affiliate.date_created)}</td>
                      <td>
                        {isActive ? (
                          <div className={styles.statusContainer}>
                            <span className={styles.statusDot + ' ' + styles.activeDot}></span>
                            <span className={styles.activated}>
                              Active since {formatDate(affiliate.date_activated)}
                            </span>
                          </div>
                        ) : (
                          <div className={styles.statusContainer}>
                            <span className={styles.statusDot + ' ' + styles.inactiveDot}></span>
                            <span className={styles.notActivated}>Not Activated</span>
                          </div>
                        )}
                      </td>
                      <td className={styles.actionCell}>
                        <button onClick={() => handleEdit(affiliate)} className={styles.editButton}>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(affiliate.id)}
                          className={styles.deleteButton}
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => handleActivationChange(affiliate)}
                          className={isActive ? styles.deactivateButton : styles.activateButton}
                        >
                          {isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
